import { useCookie } from 'react-use'
import {
  ACCOUNT_ID_OR_ALIAS_REGEX,
  getAccountFromHost,
  AUTH_REDIRECT_TOKEN_COOKIE,
} from '../login/util'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSuspenseQuery } from '@apollo/client'
import {
  GetConfigVarsDocument,
  MeDocument,
  PersistAccessTokenDocument,
  ResolveAccountAliasDocument,
} from '../../generated/graphql'
import { useEffect } from 'react'
import { RootRoute } from '..'
import { useAbsolutePath } from '../../hooks/absolute-path'
import { useTracking } from '../../hooks/tracking'
import { useMe } from '../../hooks/me'
import { REDIRECT_QUERY_PARAM } from '../../utils/constants'

export const FormLoginHandler = () => {
  const [token] = useCookie(AUTH_REDIRECT_TOKEN_COOKIE)
  const accountFromHost = getAccountFromHost()
  const navigate = useNavigate()
  const generatePath = useAbsolutePath()
  const { trackFormSuccess, track } = useTracking()
  const { me } = useMe()
  const [searchParams] = useSearchParams()
  const redirectPage = searchParams.get(REDIRECT_QUERY_PARAM)

  const isAliasInHost = !!accountFromHost.accountAlias

  const { data: resolveAccountAliasData } = useSuspenseQuery(
    ResolveAccountAliasDocument,
    {
      variables: { accountId: accountFromHost.accountId! },
      skip: isAliasInHost,
      errorPolicy: 'all',
      context: {
        headers: {
          Authorization: token,
        },
      },
    },
  )

  const resolvedAlias =
    resolveAccountAliasData?.resolveAccountAlias.accountAlias

  const isAliasLookupFinished =
    accountFromHost.accountAlias || resolvedAlias === null

  const { data: persistAccessTokenData } = useSuspenseQuery(
    PersistAccessTokenDocument,
    {
      variables: {
        accessToken: token!,
      },
      skip: !isAliasLookupFinished,
      errorPolicy: 'all',
      fetchQueries: [{ query: MeDocument }, { query: GetConfigVarsDocument }],
    },
  )

  useEffect(() => {
    if (!isAliasInHost && resolvedAlias) {
      location.host = location.host.replace(
        ACCOUNT_ID_OR_ALIAS_REGEX,
        (match, _, group) => match.replace(group, resolvedAlias),
      )
    }
  }, [accountFromHost.accountId, isAliasInHost, resolvedAlias])

  const isAliasInHostOrNonExistent = isAliasInHost || resolvedAlias === null
  const isTokenPersisted = persistAccessTokenData?.persistAccessToken

  useEffect(() => {
    if (isAliasInHostOrNonExistent && isTokenPersisted && me) {
      trackFormSuccess({ name: 'login' })
      track('authentication_succeeded', {
        category: 'authentication',
        name: 'Form authentication',
      })

      void navigate(redirectPage ?? generatePath(RootRoute.Home))
    }
  }, [
    generatePath,
    navigate,
    trackFormSuccess,
    track,
    isAliasInHostOrNonExistent,
    isTokenPersisted,
    me,
    redirectPage,
  ])

  return null
}
